/**
 * hype-countdown — Countdown timer web component.
 *
 * Attributes (all strings, passed from Liquid):
 *   title                — label shown above the timer
 *   end-date             — ISO 8601 end datetime (fixed mode)
 *   timer-mode           — "fixed" | "daily" | "session"
 *   end-behavior         — "hide" | "message" | "redirect"
 *   end-message          — text shown when timer expires (message mode)
 *   redirect-url         — URL navigated to when timer expires (redirect mode)
 *   bg-color             — background CSS color
 *   text-color           — text CSS color
 *   accent-color         — digit / accent CSS color
 *   placement            — "inline" | "sticky_top" | "sticky_bottom"
 *   display-style        — "card" | "minimal" | "flip" | "inline_text" (default: "card")
 *   inline-message       — template for inline_text mode; {timer} is replaced with
 *                          compact HH:MM:SS / DD:HH:MM:SS (default: "Hurry! Sale ends in {timer}")
 *   background-style     — "solid" | "gradient" | "glass" (default: "solid")
 *   gradient-end-color   — second color for gradient mode (default: "#333333")
 *   days-label           — label below days digit (default: "days")
 *   hours-label          — label below hours digit (default: "hrs")
 *   minutes-label        — label below minutes digit (default: "min")
 *   seconds-label        — label below seconds digit (default: "sec")
 *   size                 — "small" | "medium" | "large" (default: "small")
 *   show-progress        — "true" | "false" (default: "false") — show a thin progress bar
 *                          at the bottom of the timer indicating elapsed time percentage.
 *                          Only visible in `fixed` timer mode (requires a known start + end).
 *                          Hidden automatically for `daily` and `session` modes.
 *   cta-label            — text for the call-to-action button (hidden when empty)
 *   cta-url              — URL the CTA button navigates to (opens in new tab)
 *   cta-style            — "solid" | "outline" | "link" (default: "solid")
 *   dir                  — "ltr" | "rtl" | "" (auto-detected from locale)
 *   locale               — BCP 47 locale tag (e.g. "ar", "he", "fa", "en")
 *   numeral-system       — "western" | "arabic" | "devanagari" | "auto" (default: "western")
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { hexToRgba } from './utils.js';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function parseEndDate(
  endDate: string,
  timerMode: string,
): Date | null {
  if (timerMode === 'fixed') {
    const d = new Date(endDate);
    return isNaN(d.getTime()) ? null : d;
  }
  if (timerMode === 'daily') {
    // Re-use the time portion from endDate each day.
    const ref = new Date(endDate);
    if (isNaN(ref.getTime())) return null;
    const now = new Date();
    const target = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      ref.getHours(),
      ref.getMinutes(),
      ref.getSeconds(),
    );
    if (target <= now) target.setDate(target.getDate() + 1);
    return target;
  }
  if (timerMode === 'session') {
    const key = 'hype_session_end';
    const stored = sessionStorage.getItem(key);
    if (stored) {
      const d = new Date(stored);
      if (!isNaN(d.getTime()) && d > new Date()) return d;
    }
    // Default: 30-minute session timer.
    const d = new Date(Date.now() + 30 * 60 * 1000);
    sessionStorage.setItem(key, d.toISOString());
    return d;
  }
  return null;
}

function calcTimeLeft(target: Date): TimeLeft {
  const diff = Math.max(0, target.getTime() - Date.now());
  const totalSeconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

const RTL_LOCALES = new Set(['ar', 'he', 'fa', 'ur', 'ps', 'sd', 'yi', 'ckb']);

const NUMERAL_MAPS: Record<string, string[]> = {
  arabic: ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'],
  devanagari: ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'],
};

function formatNumerals(str: string, system: string, locale: string): string {
  if (system === 'western') return str;
  if (system === 'auto') {
    // Use Intl.NumberFormat to detect the locale's numeral system
    try {
      const formatted = new Intl.NumberFormat(locale || 'en').format(0);
      if (formatted === '٠') return formatNumerals(str, 'arabic', locale);
      if (formatted === '०') return formatNumerals(str, 'devanagari', locale);
    } catch { /* fall through */ }
    return str;
  }
  const map = NUMERAL_MAPS[system];
  if (!map) return str;
  return str.replace(/[0-9]/g, d => map[parseInt(d, 10)]);
}


@customElement('hype-countdown')
export class HypeCountdown extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: inherit;
      /* Pin font-size so em-based sizing is immune to Dawn's 62.5% rem trick.
         Shopify themes set html { font-size: 62.5% } making 1em = 10px.
         Shadow DOM rem resolves against document root, but em resolves against
         the element's own font-size — so pinning :host insulates the widget. */
      font-size: 16px;
      /* Reserve 52px to match the Liquid template host element min-height,
         preventing CLS while the component initialises. */
      min-height: 52px;
      overflow: hidden;
      container-type: inline-size;
    }
    .bar {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 1em;
      padding: 0.75em 1em;
      background: var(--hype-bg, var(--color-background, #111));
      color: var(--hype-text, var(--color-foreground, #fff));
      flex-wrap: wrap;
      min-height: 52px;
      box-sizing: border-box;
      position: relative;
    }
    .label {
      font-size: 0.9em;
      font-weight: 500;
    }
    .digits {
      display: flex;
      gap: 0.25em;
      align-items: center;
      min-width: 0;
      flex-shrink: 1;
    }
    .unit {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .digit {
      font-size: 1.5em;
      font-weight: 700;
      color: var(--hype-accent, var(--color-accent, #ff4500));
      line-height: 1;
      min-width: 2ch;
      text-align: center;
      font-variant-numeric: tabular-nums;
      font-feature-settings: "tnum";
    }
    .unit-label {
      font-size: 0.6em;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      opacity: 0.7;
    }
    .sep {
      font-size: 1.5em;
      font-weight: 700;
      color: var(--hype-accent, var(--color-accent, #ff4500));
      line-height: 1;
      margin-bottom: 0.9em;
      font-variant-numeric: tabular-nums;
      font-feature-settings: "tnum";
    }
    .message {
      font-size: 1em;
      font-weight: 500;
      padding: 0.75em 1em;
      text-align: center;
      background: var(--hype-bg, var(--color-background, #111));
      color: var(--hype-text, var(--color-foreground, #fff));
    }

    /* ── Gradient background mode ─────────────────────────────────── */
    :host([background-style="gradient"]) .bar {
      background: linear-gradient(
        135deg,
        var(--hype-bg, #111111),
        var(--hype-gradient-end, #333333)
      ) !important;
    }
    :host([background-style="gradient"]) .message {
      background: linear-gradient(
        135deg,
        var(--hype-bg, #111111),
        var(--hype-gradient-end, #333333)
      ) !important;
    }

    /* ── Glassmorphism background mode ───────────────────────────── */
    :host([background-style="glass"]) .bar {
      background: var(--hype-bg-glass, rgba(17, 17, 17, 0.7)) !important;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.18);
    }
    :host([background-style="glass"]) .message {
      background: var(--hype-bg-glass, rgba(17, 17, 17, 0.7)) !important;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
    }

    /* ── Card variant ─────────────────────────────────────────────── */
    :host([display-style="card"]) .digit {
      font-size: 1.8em;
    }
    :host([display-style="card"]) .sep {
      font-size: 1.8em;
    }
    :host([display-style="card"]) .unit {
      background: rgba(255, 255, 255, 0.08);
      border-radius: 12px;
      padding: 8px 12px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    /* ── Minimal variant ──────────────────────────────────────────── */
    :host([display-style="minimal"]) .digit {
      letter-spacing: -0.02em;
    }
    :host([display-style="minimal"]) .sep {
      opacity: 0.3;
    }
    :host([display-style="minimal"]) .unit-label {
      text-transform: lowercase;
      font-weight: 400;
    }

    /* ── Size: medium ──────────────────────────────────────────────── */
    :host([size="medium"]) .bar {
      padding: 1em 1.25em;
      gap: 1.25em;
      min-height: 68px;
    }
    :host([size="medium"]) .digit {
      font-size: 2.2em;
    }
    :host([size="medium"]) .sep {
      font-size: 2.2em;
      margin-bottom: 1.1em;
    }
    :host([size="medium"]) .label {
      font-size: 1.1em;
    }
    :host([size="medium"]) .unit-label {
      font-size: 0.75em;
    }
    :host([size="medium"][display-style="card"]) .digit,
    :host([size="medium"][display-style="flip"]) .digit {
      font-size: 2.6em;
    }
    :host([size="medium"][display-style="card"]) .sep,
    :host([size="medium"][display-style="flip"]) .sep {
      font-size: 2.6em;
      margin-bottom: 1.2em;
    }
    :host([size="medium"][display-style="card"]) .unit {
      padding: 10px 16px;
      border-radius: 14px;
    }

    /* ── Size: large ───────────────────────────────────────────────── */
    :host([size="large"]) .bar {
      padding: 1.25em 1.5em;
      gap: 1.5em;
      min-height: 88px;
    }
    :host([size="large"]) .digit {
      font-size: 3em;
    }
    :host([size="large"]) .sep {
      font-size: 3em;
      margin-bottom: 1.3em;
    }
    :host([size="large"]) .label {
      font-size: 1.3em;
    }
    :host([size="large"]) .unit-label {
      font-size: 0.85em;
    }
    :host([size="large"][display-style="card"]) .digit,
    :host([size="large"][display-style="flip"]) .digit {
      font-size: 3.4em;
    }
    :host([size="large"][display-style="card"]) .sep,
    :host([size="large"][display-style="flip"]) .sep {
      font-size: 3.4em;
      margin-bottom: 1.4em;
    }
    :host([size="large"][display-style="card"]) .unit {
      padding: 12px 20px;
      border-radius: 16px;
    }

    /* ── RTL support ────────────────────────────────────────── */
    :host([dir="rtl"]) .bar,
    :host([dir="rtl"]) .inline-text-bar {
      direction: rtl;
    }
    :host([dir="rtl"]) .digits {
      direction: ltr; /* Digits always read left-to-right */
    }
    :host([dir="rtl"]) .dismiss-btn {
      right: auto;
      left: 8px;
    }
    :host([dir="rtl"]) .cta-btn {
      margin-inline-start: 0;
    }

    /* ── Flip variant ─────────────────────────────────────────────── */
    :host([display-style="flip"]) .digits {
      perspective: 300px;
    }
    :host([display-style="flip"]) .digit {
      font-size: 1.8em;
      position: relative;
      display: inline-block;
      min-width: 2ch;
    }
    :host([display-style="flip"]) .digit-wrap {
      position: relative;
      display: inline-grid;
      transform-style: preserve-3d;
      font-variant-numeric: tabular-nums;
      font-feature-settings: "tnum";
    }
    /* Both halves stack in the same grid cell. clip-path slices each to
       its half. Grid intrinsic sizing keeps width matched to content. */
    :host([display-style="flip"]) .flip-top,
    :host([display-style="flip"]) .flip-bottom {
      grid-area: 1 / 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px 6px;
      line-height: 1;
      font-size: inherit;
      font-weight: inherit;
      color: inherit;
      font-variant-numeric: tabular-nums;
      font-feature-settings: "tnum";
      box-sizing: border-box;
    }
    :host([display-style="flip"]) .flip-top {
      clip-path: inset(0 0 50% 0);
      background: linear-gradient(to bottom, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 100%);
      border-radius: 4px 4px 0 0;
      box-shadow: inset 0 2px 4px rgba(0,0,0,0.25);
    }
    :host([display-style="flip"]) .flip-bottom {
      clip-path: inset(50% 0 0 0);
      background: linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.25) 100%);
      border-radius: 0 0 4px 4px;
      box-shadow: inset 0 -2px 4px rgba(0,0,0,0.25);
    }
    /* Hinge line: 1px separator between halves */
    :host([display-style="flip"]) .digit-wrap::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 50%;
      height: 1px;
      background: rgba(0, 0, 0, 0.4);
      z-index: 1;
      pointer-events: none;
    }
    :host([display-style="flip"]) .digit-wrap.flipping .flip-top {
      animation: flipDown 500ms ease-in forwards;
      transform-origin: bottom center;
    }

    @keyframes flipDown {
      0%   { transform: rotateX(0deg); }
      100% { transform: rotateX(-90deg); }
    }

    /* ── Inline-text variant ──────────────────────────────────────── */
    .inline-text-bar {
      display: block;
      text-align: center;
      font-size: 1em;
      font-weight: 500;
      padding: 0.75em 1em;
      background: var(--hype-bg, var(--color-background, #111));
      color: var(--hype-text, var(--color-foreground, #fff));
      min-height: 52px;
      box-sizing: border-box;
      position: relative;
      transition: background-color 2s ease, box-shadow 2s ease;
    }
    .inline-timer {
      font-weight: 700;
      color: var(--hype-accent, var(--color-accent, #ff4500));
      background: var(--hype-accent-bg, rgba(255, 69, 0, 0.15));
      padding: 0.1em 0.4em;
      border-radius: 4px;
      font-variant-numeric: tabular-nums;
      font-feature-settings: "tnum";
    }

    /* Inline-text size variants */
    :host([size="medium"][display-style="inline_text"]) .inline-text-bar {
      font-size: 1.2em;
      padding: 1em 1.25em;
      min-height: 68px;
    }
    :host([size="large"][display-style="inline_text"]) .inline-text-bar {
      font-size: 1.5em;
      padding: 1.25em 1.5em;
      min-height: 88px;
    }

    /* Inline-text gradient background */
    :host([background-style="gradient"][display-style="inline_text"]) .inline-text-bar {
      background: linear-gradient(
        135deg,
        var(--hype-bg, #111111),
        var(--hype-gradient-end, #333333)
      ) !important;
    }

    /* Inline-text glass background */
    :host([background-style="glass"][display-style="inline_text"]) .inline-text-bar {
      background: var(--hype-bg-glass, rgba(17, 17, 17, 0.7)) !important;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.18);
    }

    /* Inline-text urgency escalation */
    .inline-text-bar.warning .inline-timer {
      color: #f97316;
      background: rgba(249, 115, 22, 0.15);
      transition: color 2s ease, background 2s ease;
    }
    .inline-text-bar.urgent .inline-timer {
      color: #ef4444;
      background: rgba(239, 68, 68, 0.15);
      transition: color 0.5s ease, background 0.5s ease;
    }
    .inline-text-bar.urgent {
      box-shadow: 0 0 0 0 rgba(239,68,68,0);
      animation: urgentPulse 2s ease-in-out infinite;
    }
    .inline-text-bar.critical .inline-timer {
      color: #dc2626;
      font-weight: 800;
      background: rgba(220, 38, 38, 0.15);
      transition: color 0.3s ease, background 0.3s ease;
    }
    .inline-text-bar.critical {
      animation: criticalPulse 1s ease-in-out infinite;
    }

    /* Inline-text entry / exit animations */
    .inline-text-bar.entering-sticky-top {
      animation: slideInTop 300ms ease-out both;
    }
    .inline-text-bar.entering-sticky-bottom {
      animation: slideInBottom 300ms ease-out both;
    }
    .inline-text-bar.entering-inline {
      animation: fadeIn 400ms ease-out both;
    }
    .inline-text-bar.exiting-sticky-top {
      animation: slideOutTop 250ms ease-in both;
    }
    .inline-text-bar.exiting-sticky-bottom {
      animation: slideOutBottom 250ms ease-in both;
    }
    .inline-text-bar.exiting-inline {
      animation: fadeOut 300ms ease-in both;
    }

    /* ── Narrow container scaling ──────────────────────────────── */
    /* Scale down smoothly in narrow containers (e.g. admin preview panel).
       Container queries respond to the host element width, not the viewport. */
    @container (max-width: 340px) {
      .bar {
        gap: 0.5em;
        padding: 0.5em 0.75em;
      }
      .digits {
        gap: 0.15em;
      }
      .digit {
        font-size: 1.15em;
        min-width: 1.8ch;
      }
      .sep {
        font-size: 1.15em;
        margin-bottom: 0.6em;
      }
      .unit-label {
        font-size: 0.5em;
      }
      .label {
        font-size: 0.78em;
      }
    }
    @container (max-width: 340px) {
      :host([display-style="card"]) .digit {
        font-size: 1.3em;
      }
      :host([display-style="card"]) .sep {
        font-size: 1.3em;
      }
      :host([display-style="card"]) .unit {
        padding: 4px 6px;
        border-radius: 8px;
      }
      :host([display-style="flip"]) .digit {
        font-size: 1.3em;
      }
      :host([display-style="flip"]) .sep {
        font-size: 1.3em;
      }
    }
    @container (max-width: 250px) {
      .bar {
        gap: 0.3em;
        padding: 0.4em 0.5em;
      }
      .digits {
        gap: 0.1em;
      }
      .digit {
        font-size: 0.95em;
        min-width: 1.6ch;
      }
      .sep {
        font-size: 0.95em;
        margin-bottom: 0.5em;
      }
      .unit-label {
        font-size: 0.45em;
      }
      .label {
        font-size: 0.7em;
      }
    }

    /* Mobile-first responsive — narrow viewports stack label above digits
       and use tighter font sizes / padding to stay within one line. */
    @media (max-width: 480px) {
      .bar {
        flex-direction: column;
        gap: 0.4em;
        padding: 0.5em 0.75em;
      }
      .label {
        font-size: 0.78em;
        text-align: center;
      }
      .digit {
        font-size: 1.15em;
      }
      .sep {
        font-size: 1.15em;
        margin-bottom: 0.7em;
      }
      .unit-label {
        font-size: 0.55em;
      }
      /* Medium on mobile — scale down proportionally */
      :host([size="medium"]) .bar {
        padding: 0.65em 0.85em;
        gap: 0.5em;
        min-height: 60px;
      }
      :host([size="medium"]) .digit,
      :host([size="medium"][display-style="card"]) .digit,
      :host([size="medium"][display-style="flip"]) .digit {
        font-size: 1.6em;
      }
      :host([size="medium"]) .sep,
      :host([size="medium"][display-style="card"]) .sep,
      :host([size="medium"][display-style="flip"]) .sep {
        font-size: 1.6em;
        margin-bottom: 0.85em;
      }
      :host([size="medium"]) .label {
        font-size: 0.9em;
      }
      :host([size="medium"]) .unit-label {
        font-size: 0.65em;
      }
      /* Large on mobile — still bigger but fits screen */
      :host([size="large"]) .bar {
        padding: 0.75em 1em;
        gap: 0.6em;
        min-height: 72px;
      }
      :host([size="large"]) .digit,
      :host([size="large"][display-style="card"]) .digit,
      :host([size="large"][display-style="flip"]) .digit {
        font-size: 2em;
      }
      :host([size="large"]) .sep,
      :host([size="large"][display-style="card"]) .sep,
      :host([size="large"][display-style="flip"]) .sep {
        font-size: 2em;
        margin-bottom: 1em;
      }
      :host([size="large"]) .label {
        font-size: 1em;
      }
      :host([size="large"]) .unit-label {
        font-size: 0.7em;
      }
      .cta-btn { font-size: 0.78em; padding: 0.4em 1em; min-height: 40px; }
      /* Inline-text on mobile — scale down proportionally */
      :host([display-style="inline_text"]) .inline-text-bar {
        font-size: 0.85em;
        padding: 0.5em 0.75em;
      }
      :host([size="medium"][display-style="inline_text"]) .inline-text-bar {
        font-size: 1em;
        padding: 0.65em 0.85em;
        min-height: 60px;
      }
      :host([size="large"][display-style="inline_text"]) .inline-text-bar {
        font-size: 1.2em;
        padding: 0.75em 1em;
        min-height: 72px;
      }
    }

    /* ── Mobile bottom sheet for sticky-bottom ────────────────── */
    @media (max-width: 480px) {
      :host([placement="sticky_bottom"]) .bar {
        border-radius: 16px 16px 0 0;
        box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.15);
        padding: 0.75em 1em;
        padding-bottom: max(0.75em, env(safe-area-inset-bottom));
      }
      :host([placement="sticky_bottom"]) .digit {
        font-size: 1.3em;
      }
      :host([placement="sticky_bottom"]) .sep {
        font-size: 1.3em;
        margin-bottom: 0.8em;
      }
      :host([placement="sticky_bottom"]) .unit-label {
        font-size: 0.65em;
      }
      :host([placement="sticky_bottom"]) .dismiss-btn {
        width: 36px;
        height: 36px;
      }
    }

    /* Very narrow phones — further reduce to avoid overflow. */
    @media (max-width: 320px) {
      .digit {
        font-size: 0.95em;
        min-width: 1.8ch;
      }
      .sep {
        font-size: 0.95em;
        margin-bottom: 0.55em;
      }
      .digits {
        gap: 0.15em;
      }
      :host([placement="sticky_bottom"]) .digit {
        font-size: 1.1em;
      }
      :host([placement="sticky_bottom"]) .sep {
        font-size: 1.1em;
      }
    }

    /* ── Number change micro-animation ───────────────────────────── */
    @keyframes hype-num-pulse {
      0%   { transform: scale(1); }
      50%  { transform: scale(1.15); }
      100% { transform: scale(1); }
    }
    .digit.num-pulse {
      animation: hype-num-pulse 180ms ease-out both;
    }

    /* ── Near-expiry escalation ──────────────────────────────────── */
    .bar {
      transition: background-color 2s ease, box-shadow 2s ease;
    }
    .bar.warning .digit,
    .bar.warning .sep {
      color: #f97316;
      transition: color 2s ease;
    }

    .bar.urgent .digit,
    .bar.urgent .sep {
      color: #ef4444;
      transition: color 0.5s ease;
    }
    .bar.urgent {
      box-shadow: 0 0 0 0 rgba(239,68,68,0);
      animation: urgentPulse 2s ease-in-out infinite;
    }
    :host([display-style="card"]) .bar.urgent .unit {
      background: rgba(239,68,68,0.08);
    }

    .bar.critical .digit,
    .bar.critical .sep {
      color: #dc2626;
      font-weight: 800;
      transition: color 0.3s ease;
    }
    .bar.critical .label {
      color: #dc2626;
    }
    .bar.critical {
      animation: criticalPulse 1s ease-in-out infinite;
    }
    :host([display-style="card"]) .bar.critical .unit {
      background: rgba(220,38,38,0.08);
    }

    @keyframes urgentPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
      50% { box-shadow: 0 0 12px 4px rgba(239,68,68,0.3); }
    }
    @keyframes criticalPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0); transform: scale(1); }
      50% { box-shadow: 0 0 16px 6px rgba(220,38,38,0.35); transform: scale(1.02); }
    }

    /* ── Dismiss button ─────────────────────────────────────────── */
    .dismiss-btn {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      color: var(--hype-text, var(--color-foreground, #fff));
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.2;
      transition: opacity 0.2s ease;
      border-radius: 4px;
      padding: 0;
    }
    /* 44px touch target via pseudo-element */
    .dismiss-btn::before {
      content: '';
      position: absolute;
      width: 44px;
      height: 44px;
    }
    .bar:hover .dismiss-btn {
      opacity: 0.4;
    }
    .dismiss-btn:hover {
      opacity: 0.7;
    }
    .dismiss-btn:focus-visible {
      opacity: 0.7;
      outline: 2px solid var(--hype-accent, var(--color-accent, #ff4500));
      outline-offset: 2px;
    }

    /* ── CTA button ───────────────────────────────────────────── */
    .cta-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.5em 1.2em;
      border-radius: 6px;
      font-size: 0.85em;
      font-weight: 600;
      text-decoration: none;
      cursor: pointer;
      transition: opacity 0.2s ease, transform 0.1s ease;
      line-height: 1.2;
      white-space: nowrap;
      min-height: 44px; /* touch target */
      border: none;
    }
    .cta-btn:hover {
      opacity: 0.85;
      transform: translateY(-1px);
    }
    .cta-btn:active {
      transform: translateY(0);
    }
    .cta-btn:focus-visible {
      outline: 2px solid var(--hype-accent, #ff4500);
      outline-offset: 2px;
    }
    .cta-btn.cta-solid {
      background: var(--hype-accent, #ff4500);
      color: var(--hype-bg, #111);
    }
    .cta-btn.cta-outline {
      background: transparent;
      border: 2px solid var(--hype-text, #fff);
      color: var(--hype-text, #fff);
    }
    .cta-btn.cta-link {
      background: transparent;
      color: var(--hype-text, #fff);
      text-decoration: underline;
      padding: 0.5em 0.4em;
    }
    :host([size="medium"]) .cta-btn { font-size: 0.95em; padding: 0.55em 1.4em; }
    :host([size="large"]) .cta-btn { font-size: 1.05em; padding: 0.6em 1.6em; }

    /* ── Progress bar ────────────────────────────────────────────── */
    .progress-bar {
      position: absolute;
      bottom: 0;
      left: 0;
      height: 3px;
      background: var(--hype-accent, #ff4500);
      opacity: 0.3;
      transition: width 1s linear;
      border-radius: 0 2px 0 0;
    }

    /* ── Entry / exit animations ─────────────────────────────────── */

    /* Keyframes */
    @keyframes slideInTop {
      from { transform: translateY(-100%); }
      to   { transform: translateY(0); }
    }
    @keyframes slideInBottom {
      from { transform: translateY(100%); }
      to   { transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes slideOutTop {
      from { transform: translateY(0); }
      to   { transform: translateY(-100%); }
    }
    @keyframes slideOutBottom {
      from { transform: translateY(0); }
      to   { transform: translateY(100%); }
    }
    @keyframes fadeOut {
      from { opacity: 1; }
      to   { opacity: 0; }
    }

    /* Entry */
    .bar.entering-sticky-top {
      animation: slideInTop 300ms ease-out both;
    }
    .bar.entering-sticky-bottom {
      animation: slideInBottom 300ms ease-out both;
    }
    .bar.entering-inline {
      animation: fadeIn 400ms ease-out both;
    }

    /* Exit */
    .bar.exiting-sticky-top {
      animation: slideOutTop 250ms ease-in both;
    }
    .bar.exiting-sticky-bottom {
      animation: slideOutBottom 250ms ease-in both;
    }
    .bar.exiting-inline {
      animation: fadeOut 300ms ease-in both;
    }

    /* ── Powered by Hype branding ────────────────────────────── */
    :host([placement="inline"]) {
      border-radius: 0.5em;
      overflow: hidden;
    }
    .hype-branding {
      display: block;
      text-align: center;
      padding: 0.15em 0;
      background: var(--hype-bg, inherit);
    }
    .hype-branding a {
      font-size: 0.625em;
      color: var(--hype-text, var(--color-foreground, #fff));
      opacity: 0.4;
      text-decoration: none;
      letter-spacing: 0.02em;
    }
    .hype-branding a:hover {
      opacity: 0.65;
      text-decoration: underline;
    }

    @media (prefers-reduced-motion: reduce) {
      :host, .bar, .digits, .digit, .inline-text-bar, .inline-timer {
        animation: none !important;
        transition: none !important;
      }
      .digit.num-pulse {
        animation: none !important;
      }
      :host([display-style="flip"]) .digit-wrap.flipping .flip-top {
        animation: none !important;
      }
      .bar.urgent, .bar.critical,
      .inline-text-bar.urgent, .inline-text-bar.critical {
        animation: none !important;
        transform: none !important;
      }
      .bar.warning .digit, .bar.warning .sep,
      .bar.urgent .digit, .bar.urgent .sep,
      .bar.critical .digit, .bar.critical .sep {
        transition: none !important;
      }
      .inline-text-bar.warning .inline-timer,
      .inline-text-bar.urgent .inline-timer,
      .inline-text-bar.critical .inline-timer {
        transition: none !important;
      }
      .dismiss-btn {
        transition: none !important;
      }
      .cta-btn {
        transition: none !important;
      }
      .progress-bar {
        transition: none !important;
      }
      /* Disable entry/exit animations */
      .bar.entering-sticky-top,
      .bar.entering-sticky-bottom,
      .bar.entering-inline,
      .bar.exiting-sticky-top,
      .bar.exiting-sticky-bottom,
      .bar.exiting-inline,
      .inline-text-bar.entering-sticky-top,
      .inline-text-bar.entering-sticky-bottom,
      .inline-text-bar.entering-inline,
      .inline-text-bar.exiting-sticky-top,
      .inline-text-bar.exiting-sticky-bottom,
      .inline-text-bar.exiting-inline {
        animation: none !important;
      }
    }
  `;

  @property({ attribute: 'title' }) timerTitle = '';
  @property({ attribute: 'end-date' }) endDate = '';
  @property({ attribute: 'timer-mode' }) timerMode = 'fixed';
  @property({ attribute: 'end-behavior' }) endBehavior = 'hide';
  @property({ attribute: 'end-message' }) endMessage = 'This offer has ended.';
  @property({ attribute: 'redirect-url' }) redirectUrl = '';
  @property({ attribute: 'bg-color' }) bgColor = '#111111';
  @property({ attribute: 'text-color' }) textColor = '#ffffff';
  @property({ attribute: 'accent-color' }) accentColor = '#ff4500';
  @property({ reflect: true }) placement = 'inline';
  @property({ attribute: 'display-style' }) displayStyle = 'card';
  @property({ attribute: 'background-style' }) backgroundStyle = 'solid';
  @property({ attribute: 'gradient-end-color' }) gradientEndColor = '#333333';
  @property({ attribute: 'days-label' }) daysLabel = 'days';
  @property({ attribute: 'hours-label' }) hoursLabel = 'hrs';
  @property({ attribute: 'minutes-label' }) minutesLabel = 'min';
  @property({ attribute: 'seconds-label' }) secondsLabel = 'sec';
  @property({ reflect: true }) size = 'small';
  @property({ attribute: 'show-progress' }) showProgress = 'false';
  @property({ attribute: 'inline-message' }) inlineMessage = 'Hurry! Sale ends in {timer}';
  @property({ attribute: 'cta-label' }) ctaLabel = '';
  @property({ attribute: 'cta-url' }) ctaUrl = '';
  @property({ attribute: 'cta-style' }) ctaStyle = 'solid'; // 'solid' | 'outline' | 'link'
  @property({ reflect: true }) dir = '';  // 'ltr' | 'rtl' | '' (auto)
  @property() locale = '';  // e.g. 'ar', 'he', 'fa', 'en'
  @property({ attribute: 'numeral-system' }) numeralSystem = 'western'; // 'western' | 'arabic' | 'devanagari' | 'auto'
  @property({ attribute: 'hide-branding' }) hideBranding = 'false';
  @property({ attribute: 'custom-css' }) customCss = '';

  @state() private timeLeft: TimeLeft = { days: 0, hours: 0, minutes: 0, seconds: 0 };
  @state() private expired = false;
  @state() private _dismissed = false;
  @state() private _urgency: '' | 'warning' | 'urgent' | 'critical' = '';
  @state() private _entering = false;
  @state() private _exiting = false;
  @state() private _progressPercent = 0;

  // Flip variant: track previous padded digit strings per slot to detect changes.
  // Keys: 'days' | 'hours' | 'minutes' | 'seconds'
  private _prevDigits: Record<string, string> = {};
  // Track which digit slots are currently mid-flip (have the .flipping class).
  private _flipping: Set<string> = new Set();
  // Store the old digit value during a flip so flip-top shows the departing number.
  private _flipOldDigits: Record<string, string> = {};
  // Track which digit slots are currently mid-pulse (non-flip variants).
  private _pulsing: Set<string> = new Set();
  // Track previous digit values for non-flip pulse detection.
  private _prevNonFlipDigits: Record<string, string> = {};

  private _boundKeydown!: (e: KeyboardEvent) => void;
  private _customStyleEl: HTMLStyleElement | null = null;

  private _timer: ReturnType<typeof setInterval> | null = null;
  private _target: Date | null = null;
  private _startTime = 0;
  private _totalDuration = 0;

  connectedCallback() {
    super.connectedCallback();
    if (this.placement !== 'inline' && sessionStorage.getItem('hype_countdown_dismissed') === 'true') {
      this._dismissed = true;
      return; // Don't start the timer at all
    }
    this._boundKeydown = this._handleKeydown.bind(this);
    this.addEventListener('keydown', this._boundKeydown);
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
    // Auto-detect RTL from locale if dir not explicitly set
    if (!this.dir && this.locale) {
      const lang = this.locale.split('-')[0].toLowerCase();
      if (RTL_LOCALES.has(lang)) {
        this.dir = 'rtl';
      }
    }
    this._target = parseEndDate(this.endDate, this.timerMode);
    if (this.timerMode === 'fixed' && this._target !== null) {
      this._startTime = Date.now();
      this._totalDuration = this._target.getTime() - this._startTime;
      // If end date is already past, don't track progress.
      if (this._totalDuration < 0) this._totalDuration = 0;
    }
    this._tick();
    this._timer = setInterval(() => this._tick(), 1000);
    // Trigger entry animation on first render.
    this._entering = true;
    // Entry animation durations: sticky = 300ms, inline = 400ms.
    const entryDuration = this.placement === 'inline' ? 400 : 300;
    setTimeout(() => { this._entering = false; }, entryDuration);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._timer !== null) clearInterval(this._timer);
    if (this._boundKeydown) this.removeEventListener('keydown', this._boundKeydown);
  }

  override updated(changed: Map<string, unknown>) {
    super.updated(changed);
    if (changed.has('customCss')) {
      this._applyCustomCss();
    }
  }

  private _applyCustomCss() {
    if (!this.shadowRoot) return;
    if (this.customCss) {
      if (!this._customStyleEl) {
        this._customStyleEl = document.createElement('style');
        this.shadowRoot.appendChild(this._customStyleEl);
      }
      this._customStyleEl.textContent = this.customCss;
    } else if (this._customStyleEl) {
      this._customStyleEl.remove();
      this._customStyleEl = null;
    }
  }

  private _tick() {
    if (!this._target) {
      this.expired = true;
      if (this._timer !== null) clearInterval(this._timer);
      this._handleExpiry();
      return;
    }
    const left = calcTimeLeft(this._target);
    const done = left.days === 0 && left.hours === 0 && left.minutes === 0 && left.seconds === 0;
    if (done) {
      this.expired = true;
      if (this._timer !== null) clearInterval(this._timer);
      this._handleExpiry();
    } else {
      const totalSec = left.days * 86400 + left.hours * 3600 + left.minutes * 60 + left.seconds;
      if (totalSec <= 60) this._urgency = 'critical';
      else if (totalSec <= 300) this._urgency = 'urgent';
      else if (totalSec <= 1800) this._urgency = 'warning';
      else this._urgency = '';
      if (this._totalDuration > 0) {
        const elapsed = Date.now() - this._startTime;
        this._progressPercent = Math.min(100, Math.max(0, (elapsed / this._totalDuration) * 100));
      }
      if (this.displayStyle === 'flip') {
        this._triggerFlips(left);
      } else {
        this._triggerPulses(left);
      }
      this.timeLeft = left;
    }
  }

  private _triggerFlips(next: TimeLeft) {
    const slots: Array<[string, number]> = [
      ['days',    next.days],
      ['hours',   next.hours],
      ['minutes', next.minutes],
      ['seconds', next.seconds],
    ];
    for (const [key, val] of slots) {
      const padded = pad(val);
      if (this._prevDigits[key] !== undefined && this._prevDigits[key] !== padded) {
        this._flipOldDigits[key] = this._prevDigits[key];
        this._startFlip(key);
      }
      this._prevDigits[key] = padded;
    }
  }

  private _startFlip(key: string) {
    this._flipping.add(key);
    this.requestUpdate();
    // Remove the class after animation completes (500ms).
    setTimeout(() => {
      this._flipping.delete(key);
      delete this._flipOldDigits[key];
      this.requestUpdate();
    }, 500);
  }

  private _triggerPulses(next: TimeLeft) {
    const slots: Array<[string, number]> = [
      ['days',    next.days],
      ['hours',   next.hours],
      ['minutes', next.minutes],
      ['seconds', next.seconds],
    ];
    for (const [key, val] of slots) {
      const padded = pad(val);
      if (this._prevNonFlipDigits[key] !== undefined && this._prevNonFlipDigits[key] !== padded) {
        this._startPulse(key);
      }
      this._prevNonFlipDigits[key] = padded;
    }
  }

  private _startPulse(key: string) {
    if (this._pulsing.has(key)) return; // already pulsing
    this._pulsing.add(key);
    this.requestUpdate();
    setTimeout(() => {
      this._pulsing.delete(key);
      this.requestUpdate();
    }, 200);
  }

  private _handleExpiry() {
    if (this.endBehavior === 'hide') {
      // Animate out before hiding. Exit durations: sticky = 250ms, inline = 300ms.
      this._exiting = true;
      const exitDuration = this.placement === 'inline' ? 300 : 250;
      setTimeout(() => { this._dismissed = true; }, exitDuration);
    } else if (this.endBehavior === 'redirect' && this.redirectUrl) {
      window.location.href = this.redirectUrl;
    }
    // 'message' is handled in render
  }

  private _dismiss() {
    sessionStorage.setItem('hype_countdown_dismissed', 'true');
    // Animate out before dismissing. Exit durations: sticky = 250ms, inline = 300ms.
    this._exiting = true;
    const exitDuration = this.placement === 'inline' ? 300 : 250;
    setTimeout(() => { this._dismissed = true; }, exitDuration);
  }

  private _handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && this.placement !== 'inline') {
      this._dismiss();
    }
  }

  private _renderUnit(value: string, label: string, key: string) {
    const displayValue = formatNumerals(value, this.numeralSystem, this.locale);
    if (this.displayStyle === 'flip') {
      const flipping = this._flipping.has(key);
      const topValue = flipping && this._flipOldDigits[key] !== undefined
        ? formatNumerals(this._flipOldDigits[key], this.numeralSystem, this.locale) : displayValue;
      return html`
        <div class="unit">
          <span class="digit">
            <span class="digit-wrap ${flipping ? 'flipping' : ''}">
              <span class="flip-top" aria-hidden="true">${topValue}</span>
              <span class="flip-bottom" aria-hidden="true">${displayValue}</span>
            </span>
          </span>
          <span class="unit-label">${label}</span>
        </div>
      `;
    }
    const pulsing = this._pulsing.has(key);
    return html`
      <div class="unit">
        <span class="digit${pulsing ? ' num-pulse' : ''}">${displayValue}</span>
        <span class="unit-label">${label}</span>
      </div>
    `;
  }

  private _renderBranding() {
    if (this.hideBranding === 'true') return html``;
    return html`
      <div class="hype-branding">
        <a href="https://tehmop.com/hype" target="_blank" rel="noopener noreferrer">Powered by Hype</a>
      </div>
    `;
  }

  private _renderCta() {
    if (!this.ctaLabel) return html``;
    const styleClass = this.ctaStyle === 'outline' ? 'cta-outline'
      : this.ctaStyle === 'link' ? 'cta-link'
      : 'cta-solid';
    return html`
      <a class="cta-btn ${styleClass}"
         href=${this.ctaUrl}
         target="_blank"
         rel="noopener noreferrer"
      >${this.ctaLabel}</a>
    `;
  }

  private _renderInlineText() {
    if (this._dismissed) return html``;

    this.style.setProperty('--hype-bg', this.bgColor);
    this.style.setProperty('--hype-text', this.textColor);
    this.style.setProperty('--hype-accent', this.accentColor);
    this.style.setProperty('--hype-gradient-end', this.gradientEndColor);
    this.style.setProperty('--hype-bg-glass', hexToRgba(this.bgColor, 0.7));
    this.style.setProperty('--hype-accent-bg', hexToRgba(this.accentColor, 0.15));

    if (this.expired && this.endBehavior === 'message') {
      return html`<div class="message">${this.endMessage}</div>`;
    }

    const { days, hours, minutes, seconds } = this.timeLeft;
    const timerStr = days > 0
      ? `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
      : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    const displayTimer = formatNumerals(timerStr, this.numeralSystem, this.locale);

    const placementKey = this.placement === 'sticky_top' ? 'sticky-top'
      : this.placement === 'sticky_bottom' ? 'sticky-bottom'
      : 'inline';
    const animClass = this._exiting ? `exiting-${placementKey}`
      : this._entering ? `entering-${placementKey}`
      : '';

    // Split message on {timer} and interleave the timer span.
    const parts = this.inlineMessage.split('{timer}');

    return html`
      <div class="inline-text-bar ${this._urgency} ${animClass}">
        ${parts[0]}<span class="inline-timer">${displayTimer}</span>${parts.length > 1 ? parts.slice(1).join('{timer}') : ''}
        ${this._renderCta()}
        ${this.placement !== 'inline' ? html`
          <button class="dismiss-btn"
            aria-label="Dismiss countdown timer"
            @click=${this._dismiss}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        ` : ''}
      </div>
      ${this._renderBranding()}
    `;
  }

  render() {
    if (this._dismissed) return html``;

    if (this.displayStyle === 'inline_text') {
      return this._renderInlineText();
    }

    this.style.setProperty('--hype-bg', this.bgColor);
    this.style.setProperty('--hype-text', this.textColor);
    this.style.setProperty('--hype-accent', this.accentColor);
    this.style.setProperty('--hype-gradient-end', this.gradientEndColor);
    this.style.setProperty('--hype-bg-glass', hexToRgba(this.bgColor, 0.7));

    if (this.expired && this.endBehavior === 'message') {
      return html`<div class="message">${this.endMessage}</div>`;
    }

    const { days, hours, minutes, seconds } = this.timeLeft;
    const showDays = days > 0;

    const placementKey = this.placement === 'sticky_top' ? 'sticky-top'
      : this.placement === 'sticky_bottom' ? 'sticky-bottom'
      : 'inline';
    const animClass = this._exiting ? `exiting-${placementKey}`
      : this._entering ? `entering-${placementKey}`
      : '';

    return html`
      <div class="bar ${this._urgency} ${animClass}">
        ${this.timerTitle ? html`<span class="label">${this.timerTitle}</span>` : ''}
        <div class="digits">
          ${showDays ? html`
            ${this._renderUnit(pad(days), this.daysLabel, 'days')}
            <span class="sep">:</span>
          ` : ''}
          ${this._renderUnit(pad(hours), this.hoursLabel, 'hours')}
          <span class="sep">:</span>
          ${this._renderUnit(pad(minutes), this.minutesLabel, 'minutes')}
          <span class="sep">:</span>
          ${this._renderUnit(pad(seconds), this.secondsLabel, 'seconds')}
        </div>
        ${this._renderCta()}
        ${this.placement !== 'inline' ? html`
          <button class="dismiss-btn"
            aria-label="Dismiss countdown timer"
            @click=${this._dismiss}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        ` : ''}
        ${this.showProgress === 'true' && this._totalDuration > 0 ? html`
          <div class="progress-bar" style="width: ${this._progressPercent}%"></div>
        ` : ''}
      </div>
      ${this._renderBranding()}
    `;
  }
}
